import { EMessengerEventBusEvents } from '@xometry/ui';
import { amplitudeLogger } from 'amplitude';
import clsx from 'clsx';
import { ErpMessengerMicroFrontend } from 'components/MicroFrontend/ErpMessengerMicroFrontend';
import { MICRO_FRONTEND_NAME } from 'components/MicroFrontend/MicroFrontend.types';
import PreviewModal from 'components/UI/Preview/Modal';
import { ENV_MESSENGER_URL } from 'constants/env';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useStore } from 'stores/RootStore';
import { ESideOverlayType } from 'stores/SideOverlay/SideOverlayStore';
import { DeviceTypes, useDeviceType } from 'utils/hooks/useDeviceType';
import { useEmbedded } from 'utils/hooks/useEmbedded';
import { useMessengerEventBus } from 'utils/hooks/useMessengerEventBus';
import { buildDealMessengerChatSource } from 'utils/messenger/buildDealMessengerChatSource';
import { computeErpMainMessengerHostState } from 'utils/messenger/computeErpMainMessengerHostState';
import { getErpMessengerLayoutModeForMessengerPayload } from 'utils/messenger/getErpMessengerLayoutModeForPayload';
import { resolveDealIdForMessengerHostOverlays } from 'utils/messenger/resolveDealIdForMessengerHostOverlays';

import { HeaderControls } from './HeaderControls';
import s from './SideOverlayContainer.module.less';
import { ContentContainer, MessengerOverlayMfeShell } from './SideOverlayContainer.styled';

const SideOverlayContainerComponent = () => {
  const contentContainerRef = useRef<HTMLDivElement | null>(null);
  const store = useStore();

  const {
    sideOverlayStore,
    erpMessengerLayoutStore,
    previewsStore: { openPreview, closePreview, previewVisibility },
  } = store;

  const deviceType = useDeviceType();
  const previewUrl = '';

  const { isOpen, side, context, closeSideOverlay: closeSideOverlayStore, toggleSide } = sideOverlayStore;
  const { setEventBusRef, sendEvent } = useMessengerEventBus();
  const { isEmbedded } = useEmbedded();
  const { pathname, search } = useLocation();

  const isMobile = deviceType === DeviceTypes.MOBILE && !isEmbedded;
  const showHeaderControls = !isMobile && !isEmbedded;

  const dealIdForThread = resolveDealIdForMessengerHostOverlays(pathname, store);

  const liveDealThreadChatSource =
    dealIdForThread != null && dealIdForThread !== ''
      ? buildDealMessengerChatSource({ pathname, search, dealId: dealIdForThread, store })
      : null;

  const messengerHostState = computeErpMainMessengerHostState(store, {
    isMobile,
    isEmbedded,
    setEventBusRef,
    amplitudeLogger,
    liveDealThreadChatSource,
  });

  /** Classic overlay MC is open, but this thread would render as a compact column — offer a one-click switch. */
  const canOfferCompactLayoutFromClassic =
    showHeaderControls &&
    context.type === ESideOverlayType.Messenger &&
    erpMessengerLayoutStore.userErpMessengerLayoutMode === 'classic' &&
    getErpMessengerLayoutModeForMessengerPayload({
      userPreference: 'compact',
      isMobile: false,
      isEmbedded: false,
      payload: context,
    }) === 'compact';

  const handleUseSidePanel = () => {
    erpMessengerLayoutStore.setUserErpMessengerLayoutMode('compact');
    sideOverlayStore.clearMessengerLayoutPresentationOverride();
  };

  const closeSideOverlay = useCallback(() => {
    sendEvent?.(EMessengerEventBusEvents.CLOSE_MESSENGER, {});
    closePreview();
    closeSideOverlayStore();
  }, [closeSideOverlayStore, closePreview, sendEvent]);

  const handleOpenModal = () => {
    openPreview();
  };

  const previewModalComponent = (
    <PreviewModal
      visible={previewVisibility}
      openModal={handleOpenModal}
      closeModal={closePreview}
      url={previewUrl}
      skipFetch={true}
    />
  );

  if (!isOpen) {
    return previewModalComponent;
  }

  const isCompactDesktopMessenger =
    context.type === ESideOverlayType.Messenger && messengerHostState.showCompactColumnMessenger;

  if (isCompactDesktopMessenger) return previewModalComponent;

  return (
    <>
      <ContentContainer
        className={clsx(s[side], { [s.isMobile]: isMobile, [s.isEmbedded]: isEmbedded })}
        ref={contentContainerRef}
        data-testid="MessengerSideOverlay"
      >
        {showHeaderControls ? (
          <HeaderControls
            onClose={() => {
              closeSideOverlay();
            }}
            onToggleSide={toggleSide}
            side={side}
            messengerLayoutToggle={
              canOfferCompactLayoutFromClassic
                ? {
                    currentMode: 'classic',
                    onToggle: handleUseSidePanel,
                  }
                : undefined
            }
          />
        ) : null}

        {messengerHostState.showOverlayMessenger && messengerHostState.appParams ? (
          <MessengerOverlayMfeShell>
            <ErpMessengerMicroFrontend
              key={messengerHostState.microFrontendKey}
              name={MICRO_FRONTEND_NAME.Messenger}
              containerId="MessengerContainer"
              host={ENV_MESSENGER_URL}
              appParams={messengerHostState.appParams}
            />
          </MessengerOverlayMfeShell>
        ) : null}
      </ContentContainer>

      {previewModalComponent}
    </>
  );
};

export const SideOverlayContainer = observer(SideOverlayContainerComponent);
